import React from 'react';

import { Box } from '@rover/kibble/core';
import { TABLET_MAX, TABLET_MIN } from '@rover/kibble/styles/MediaQuery';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';

export type MaximalGridItem = {
  key: string;
  isFullWidth: boolean;
  node: JSX.Element;
};

type Props = {
  items: MaximalGridItem[];
};

/**
 * Reorders items so every full-width callout starts a fresh row with no empty
 * cell before it. This keeps visual order === DOM order === focus/screen-reader order
 */
export const reorderFullWidthItems = (
  items: MaximalGridItem[],
  columns: number
): MaximalGridItem[] => {
  if (columns <= 1) {
    return items;
  }

  const ordered: MaximalGridItem[] = [];
  const deferred: MaximalGridItem[] = [];
  let cardsInRow = 0;

  items.forEach((item) => {
    if (item.isFullWidth) {
      if (cardsInRow === 0) {
        // Already at a row boundary — emit immediately.
        ordered.push(item);
      } else {
        // Mid-row — hold until the current row of cards fills.
        deferred.push(item);
      }
      return;
    }

    ordered.push(item);
    cardsInRow += 1;
    if (cardsInRow === columns) {
      cardsInRow = 0;
      ordered.push(...deferred.splice(0));
    }
  });

  // Any callouts trailing a final, partially filled row.
  ordered.push(...deferred);

  return ordered;
};

const MaximalSearchCardsGrid = ({ items }: Props): JSX.Element => {
  const isTabletUp = useMatchMedia(`(min-width: ${TABLET_MIN}px)`);
  const isDesktop = useMatchMedia(`(min-width: ${TABLET_MAX + 1}px)`);

  let columns = 1;
  if (isDesktop) {
    columns = 2;
  } else if (isTabletUp) {
    columns = 3;
  }

  const ordered = reorderFullWidthItems(items, columns);

  return (
    <Box
      width="100%"
      maxWidth="100%"
      display="grid"
      mt="4x"
      sx={{
        overflow: 'hidden',
        gridTemplateColumns: 'minmax(0, 1fr)',
        [`@media (min-width: ${TABLET_MIN}px)`]: {
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)',
        },
        [`@media (min-width: ${TABLET_MAX + 1}px)`]: {
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        },
      }}
    >
      {ordered.map((item) => (
        <React.Fragment key={item.key}>{item.node}</React.Fragment>
      ))}
    </Box>
  );
};

export default MaximalSearchCardsGrid;
